import { ErrorStatus } from '../../../core/models/entities/ErrorStatus'
import { ErrorResponse } from '../responses/ErrorResponse'
import { enviroment } from '../../../enviroment'

export function mapToErrorStatus(errorDto: ErrorResponse): ErrorStatus {
  return {
    code: errorDto.code,
    message: errorDto.message,
    errorCode: errorDto.errorCode
  }
}

// Marker for a flag-gated route that answered 404 { disabled: true, flag }
// (the admin Stripe payout routes while StripePayoutReconciliationEnabled is
// off). Screens use it to tell "feature off" from "not found"/"server error".
export const DISABLED_ERROR_CODE = 'DISABLED'

// Marker of the API's ProductPendingGuard 409 ("Prices on their way", flag
// productPendingEnabled): { code: "productPending", error: "productPending",
// message, logoIds, retryAfterSeconds }.
export const PRODUCT_PENDING_ERROR_CODE = 'productPending'

// Marker of the API's fail-fast 503 while the logo supplier (app117) is
// down (flag supplierUnavailableEnabled): { code: "supplierUnavailable",
// error: "supplierUnavailable", message, retryAfterSeconds } on upload,
// clone, colour change, method change and resize.
export const SUPPLIER_UNAVAILABLE_ERROR_CODE = 'supplierUnavailable'

// The ErrorStatus for a non-OK response body. Bodies come in three shapes:
//  - the JSON envelope { code, message[, errorCode] } -> mapped as before;
//  - a JSON string (BadRequestObjectResult("...")) -> the HTTP status + text;
//  - any other JSON object ({ error }, { disabled, flag }, ...) or non-JSON
//    text -> the HTTP status, the best message the body offers, and the
//    DISABLED marker when the body says so.
// Envelope bodies keep their exact result; only bodies that used to yield
// code/message undefined now carry the HTTP status.
export function errorStatusFromBody(
  httpStatus: number,
  bodyText: string
): ErrorStatus {
  let parsed: unknown
  try {
    parsed = JSON.parse(bodyText)
  } catch {
    return { code: httpStatus, message: bodyText }
  }
  if (typeof parsed === 'string') {
    return { code: httpStatus, message: parsed }
  }
  if (parsed === null || typeof parsed !== 'object') {
    return { code: httpStatus, message: bodyText }
  }
  const body = parsed as Partial<ErrorResponse> & {
    error?: unknown
    disabled?: unknown
  }
  const status = mapToErrorStatus(body as ErrorResponse)
  if (typeof status.code !== 'number') status.code = httpStatus
  if (typeof status.message !== 'string') {
    status.message = typeof body.error === 'string' ? body.error : bodyText
  }
  if (body.disabled === true && status.errorCode === undefined) {
    status.errorCode = DISABLED_ERROR_CODE
  }
  // Flag productPendingEnabled: the guard's 409 carries its marker in the
  // STRING `code` / `error` and a human `message`. The string `code` was just
  // replaced by the HTTP status and the message never names the marker, so
  // without this the envelope would be indistinguishable from any other 409
  // and the one automatic retry would never fire. Keep the marker as
  // errorCode (nothing else sets one on this body) and carry
  // retryAfterSeconds. Flag off = the status exactly as above.
  // Flag cloneInstantEnabled: the clone-upload guard answers the SAME
  // envelope with reason "cloneUpload" - recognised on its own flag too (K3
  // may be on without R5) and the reason is carried for the toast text.
  if (
    (enviroment.productPendingEnabled || enviroment.cloneInstantEnabled) &&
    httpStatus === 409
  ) {
    const raw = body as {
      code?: unknown
      error?: unknown
      retryAfterSeconds?: unknown
      reason?: unknown
    }
    const marked = [raw.code, raw.error].some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase() === PRODUCT_PENDING_ERROR_CODE.toLowerCase()
    )
    if (marked) {
      if (status.errorCode === undefined) status.errorCode = PRODUCT_PENDING_ERROR_CODE
      const seconds = Number(raw.retryAfterSeconds)
      if (Number.isFinite(seconds) && seconds > 0) status.retryAfterSeconds = seconds
      if (enviroment.cloneInstantEnabled && typeof raw.reason === 'string' && raw.reason) {
        status.reason = raw.reason
      }
    }
  }
  // Flag logoPriceRefreshNowEnabled: the admin "Refresh prices now" route
  // answers 429 rateLimited and 409 alreadyPending with retryAfterSeconds
  // (the API's Redis claim / the pending stamp's remaining cooldown). The
  // block above carries that field only for the productPending 409, so the
  // 429 - and a 409 with another marker - would lose it and the button
  // would fall back to a flat 60 s. errorCode already comes from the
  // body's errorCode (the API sends it beside code/error for exactly this
  // reader). Never overwrites a value set above. Flag off = the status
  // exactly as above.
  if (
    enviroment.logoPriceRefreshNowEnabled &&
    (httpStatus === 429 || httpStatus === 409) &&
    status.retryAfterSeconds === undefined
  ) {
    const raw = body as { retryAfterSeconds?: unknown }
    if (
      typeof raw.retryAfterSeconds === 'number' &&
      Number.isFinite(raw.retryAfterSeconds) &&
      raw.retryAfterSeconds > 0
    ) {
      status.retryAfterSeconds = raw.retryAfterSeconds
    }
  }
  // Flag supplierUnavailableEnabled (toast / kept file) or
  // supplierStatusEnabled (the banner's immediate re-check): the API's
  // fail-fast 503 while app117 is down carries its marker in the STRING
  // `code` / `error` (replaced by the HTTP status above, exactly as the
  // productPending 409), the seconds the health latch still holds and the
  // reason ("latchOpen" = refused before any write, "timeout" /
  // "transport" = lost the supplier inside the request). Keep the marker as
  // errorCode (never overwriting an explicit one), carry retryAfterSeconds
  // and reason, so the toast/policy can tell "supplier down" from any other
  // 503. Both flags off = the status exactly as above.
  if (
    (enviroment.supplierUnavailableEnabled || enviroment.supplierStatusEnabled) &&
    httpStatus === 503
  ) {
    const raw = body as {
      code?: unknown
      error?: unknown
      retryAfterSeconds?: unknown
      reason?: unknown
    }
    const marked = [raw.code, raw.error].some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase() === SUPPLIER_UNAVAILABLE_ERROR_CODE.toLowerCase()
    )
    if (marked) {
      if (status.errorCode === undefined) {
        status.errorCode = SUPPLIER_UNAVAILABLE_ERROR_CODE
      }
      const seconds = Number(raw.retryAfterSeconds)
      if (
        status.retryAfterSeconds === undefined &&
        Number.isFinite(seconds) &&
        seconds > 0
      ) {
        status.retryAfterSeconds = seconds
      }
      if (
        status.reason === undefined &&
        typeof raw.reason === 'string' &&
        raw.reason !== ''
      ) {
        status.reason = raw.reason
      }
    }
  }
  // Flag adminPriceAdjustmentEnabled: the price-adjustment PUT answers a
  // deterministic Stripe refusal (amount below the per-currency minimum, an
  // idempotency mismatch, ...) as 400 { outcome: "invalid", error:
  // "stripeRejected", detail: <Stripe's message> }. That body has no
  // `message`, so `error` became the message above and the raw text - and
  // with it `detail` - would be gone by the time the DataError reaches the
  // repository. Carry `detail` so the dialog can tell the admin what Stripe
  // rejected instead of a bare code. Flag off = the status exactly as above.
  if (enviroment.adminPriceAdjustmentEnabled && httpStatus === 400) {
    const raw = body as { detail?: unknown }
    if (typeof raw.detail === 'string' && raw.detail.trim() !== '') {
      status.detail = raw.detail
    }
  }
  return status
}
